import { useCallback, useMemo } from 'react';
import { useOrganizationList } from '@clerk/clerk-react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useAgentsApi } from '../api/useAgentsApi';

/**
 * The MCP agents (OAuth clients) the user has authorized, plus mutations to
 * de-authorize one, toggle an agent's personal-scope access, and grant/revoke its
 * access to ANY organization the user belongs to. Access is Glue-owned (see
 * agent.service.ts), so it is toggled here per-agent rather than depending on the
 * active Clerk org.
 */
export const useAgents = () => {
    const { userMemberships, isLoaded } = useOrganizationList({ userMemberships: true });
    const { listAgents, revokeAgent: revokeAgentApi, grantAgentOrg, ungrantAgentOrg, setAgentPersonal } = useAgentsApi();
    const queryClient = useQueryClient();

    const queryResult = useQuery({ queryKey: ['agents'], queryFn: listAgents, initialData: [] });

    const orgs = useMemo(() => {
        const memberships = userMemberships?.data ?? [];
        return memberships
            .map(membership => ({ id: membership.organization.id, name: membership.organization.name }))
            .sort((first, second) => first.name.localeCompare(second.name));
    }, [userMemberships?.data]);

    const invalidate = useCallback(() => queryClient.invalidateQueries({ queryKey: ['agents'] }), [queryClient]);

    const revokeAgent = useCallback(
        async (id: string) => {
            await revokeAgentApi(id);
            await invalidate();
        },
        [revokeAgentApi, invalidate],
    );

    const setOrgAccess = useCallback(
        async (agentId: string, orgId: string, allowed: boolean) => {
            if (allowed) await grantAgentOrg(agentId, orgId);
            else await ungrantAgentOrg(agentId, orgId);
            await invalidate();
        },
        [grantAgentOrg, ungrantAgentOrg, invalidate],
    );

    const setPersonalAccess = useCallback(
        async (agentId: string, allowed: boolean) => {
            await setAgentPersonal(agentId, allowed);
            await invalidate();
        },
        [setAgentPersonal, invalidate],
    );

    return {
        agents: queryResult.data,
        isLoading: queryResult.isLoading || !isLoaded,
        orgs,
        revokeAgent,
        setOrgAccess,
        setPersonalAccess,
    };
};
